import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { DashboardEmailFilter } from '../../components/common/DashboardEmailFilter';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  ForgeReviewIssueActiveFilter,
  ForgeReviewIssueAssignmentFilter,
  ForgeReviewIssueCropRow,
  ForgeReviewIssuesChainSnapshot,
  ForgeReviewIssuesDashboardResponse,
  ForgeReviewIssueSort,
  MasterLabelReviewIssueType,
} from '../../types';

const PAGE_LIMIT = 50;
const REFRESH_ALL_CONFIRMATION_MESSAGE =
  "Refresh all Review Issues chains?\n\nThis recomputes each chain's cached crop list and can take a while. If you only need one chain, use that chain's Refresh button instead.";
const DELETE_CROP_CONFIRMATION_MESSAGE =
  'Permanently delete this crop from Forge?\n\nThis action is only available because the crop is marked for deletion.';

const ISSUE_TYPE_OPTIONS: Array<{ value: MasterLabelReviewIssueType | 'all'; label: string }> = [
  { value: 'all', label: 'All issues' },
  { value: 'missing_design', label: 'Missing design' },
  { value: 'mark_for_deletion', label: 'Mark for deletion' },
  { value: 'bad_quality', label: 'Bad quality' },
  { value: 'sop_unclear', label: 'SOP unclear' },
  { value: 'other', label: 'Other' },
];

const ASSIGNMENT_OPTIONS: Array<{ value: ForgeReviewIssueAssignmentFilter; label: string }> = [
  { value: 'all', label: 'All assignments' },
  { value: 'assigned', label: 'Assigned to design' },
  { value: 'unassigned', label: 'Unassigned' },
];

const ACTIVE_OPTIONS: Array<{ value: ForgeReviewIssueActiveFilter; label: string }> = [
  { value: 'all', label: 'All states' },
  { value: 'active', label: 'Active crops' },
  { value: 'inactive', label: 'Inactive crops' },
];

const SORT_OPTIONS: Array<{ value: ForgeReviewIssueSort; label: string }> = [
  { value: 'updated_newest', label: 'Updated newest' },
  { value: 'created_newest', label: 'Created newest' },
  { value: 'issue_type', label: 'Issue type' },
  { value: 'assigned_first', label: 'Assigned first' },
  { value: 'barcode', label: 'Barcode' },
];

const CHAIN_BREAKDOWN_METRICS = [
  {
    key: 'missingDesign',
    label: 'Missing design',
    color: '#e6a847',
    getValue: (chain: ForgeReviewIssuesChainSnapshot) => chain.missingDesignCount,
  },
  {
    key: 'deletion',
    label: 'Deletion',
    color: '#ee7565',
    getValue: (chain: ForgeReviewIssuesChainSnapshot) => chain.markForDeletionCount,
  },
  {
    key: 'other',
    label: 'Other issues',
    color: '#9fb0ff',
    getValue: (chain: ForgeReviewIssuesChainSnapshot) => otherIssueCount(chain),
  },
] as const;

function formatNumber(value?: number | null): string {
  return (value ?? 0).toLocaleString();
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function formatRelativeAge(ageSeconds: number): string {
  if (ageSeconds < 60) return 'just now';
  if (ageSeconds < 3600) return `${Math.floor(ageSeconds / 60)}m ago`;
  if (ageSeconds < 86400) {
    const hours = Math.floor(ageSeconds / 3600);
    const minutes = Math.floor((ageSeconds % 3600) / 60);
    return `${hours}h ${String(minutes).padStart(2, '0')}m ago`;
  }
  const days = Math.floor(ageSeconds / 86400);
  const hours = Math.floor((ageSeconds % 86400) / 3600);
  return hours > 0 ? `${days}d ${hours}h ago` : `${days}d ago`;
}

function interpolateColor(
  start: [number, number, number],
  end: [number, number, number],
  ratio: number
): string {
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  const [red, green, blue] = start.map((channel, index) =>
    Math.round(channel + (end[index] - channel) * clampedRatio)
  );
  return `rgb(${red}, ${green}, ${blue})`;
}

function snapshotAgeColor(ageSeconds: number): string {
  const oneHour = 60 * 60;
  const oneDay = 24 * oneHour;
  const twoDays = 2 * oneDay;

  if (ageSeconds <= oneHour) {
    return interpolateColor([65, 195, 91], [138, 183, 70], ageSeconds / oneHour);
  }
  if (ageSeconds <= oneDay) {
    return interpolateColor(
      [138, 183, 70],
      [238, 163, 55],
      (ageSeconds - oneHour) / (oneDay - oneHour)
    );
  }
  if (ageSeconds <= twoDays) {
    return interpolateColor([238, 163, 55], [232, 82, 63], (ageSeconds - oneDay) / oneDay);
  }
  return interpolateColor(
    [232, 82, 63],
    [255, 55, 85],
    Math.min(1, (ageSeconds - twoDays) / twoDays)
  );
}

function formatSnapshotStatus(status: string): string | null {
  if (status === 'available') return null;
  if (status === 'stale') return 'Stale';
  if (status === 'missing') return 'No cached snapshot';
  return status;
}

function shortId(value?: string | null): string {
  if (!value) return '-';
  return value.length > 12 ? `${value.slice(0, 6)}...${value.slice(-4)}` : value;
}

function visibleRange(page: number, limit: number, total: number): string {
  if (total === 0) return '0';
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  return `${formatNumber(start)}-${formatNumber(end)}`;
}

function issueLabel(value: MasterLabelReviewIssueType): string {
  if (value === 'missing_design') return 'Missing design';
  if (value === 'mark_for_deletion') return 'Mark for deletion';
  if (value === 'bad_quality') return 'Bad quality';
  if (value === 'sop_unclear') return 'SOP unclear';
  return 'Other';
}

function totalIssues(chains: ForgeReviewIssuesChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.issueCount, 0);
}

function totalAssigned(chains: ForgeReviewIssuesChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.assignedToDesignCount, 0);
}

function totalMissingDesign(chains: ForgeReviewIssuesChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.missingDesignCount, 0);
}

function totalMarkedForDeletion(chains: ForgeReviewIssuesChainSnapshot[]): number {
  return chains.reduce((total, chain) => total + chain.markForDeletionCount, 0);
}

function otherIssueCount(chain: ForgeReviewIssuesChainSnapshot): number {
  return Math.max(0, chain.issueCount - chain.missingDesignCount - chain.markForDeletionCount);
}

function recomputeDashboard(
  previous: ForgeReviewIssuesDashboardResponse,
  updatedChain: ForgeReviewIssuesChainSnapshot
): ForgeReviewIssuesDashboardResponse {
  const chainExists = previous.chains.some(
    (chain) => chain.chain.chainId === updatedChain.chain.chainId
  );
  const chains = chainExists
    ? previous.chains.map((chain) =>
        chain.chain.chainId === updatedChain.chain.chainId ? updatedChain : chain
      )
    : [...previous.chains, updatedChain];
  const computedAtValues = chains
    .map((chain) => chain.computedAtUtc)
    .filter((value): value is string => Boolean(value));
  return {
    ...previous,
    computedAtUtc: computedAtValues.sort()[computedAtValues.length - 1] ?? previous.computedAtUtc,
    chains,
    supportedChainIds: previous.supportedChainIds.includes(updatedChain.chain.chainId)
      ? previous.supportedChainIds
      : [...previous.supportedChainIds, updatedChain.chain.chainId].sort(
          (left, right) => left - right
        ),
  };
}

function mergeChainSnapshots(
  current: ForgeReviewIssuesChainSnapshot[],
  incoming: ForgeReviewIssuesChainSnapshot[]
): ForgeReviewIssuesChainSnapshot[] {
  const incomingByChainId = new Map(incoming.map((chain) => [chain.chain.chainId, chain] as const));
  const seenChainIds = new Set<number>();
  const merged = current.map((chain) => {
    const chainId = chain.chain.chainId;
    seenChainIds.add(chainId);
    return incomingByChainId.get(chainId) ?? chain;
  });

  for (const chain of incoming) {
    const chainId = chain.chain.chainId;
    if (!seenChainIds.has(chainId)) {
      merged.push(chain);
    }
  }

  return merged;
}

function mergeSupportedChainIds(left: number[], right: number[]): number[] {
  return [...new Set([...left, ...right])].sort((a, b) => a - b);
}

function preserveChainSummariesForSelectedChain(
  previous: ForgeReviewIssuesDashboardResponse | null,
  next: ForgeReviewIssuesDashboardResponse,
  selectedChainId: number | 'all'
): ForgeReviewIssuesDashboardResponse {
  if (selectedChainId === 'all' || !previous?.chains.length) {
    return next;
  }

  return {
    ...next,
    chains: mergeChainSnapshots(previous.chains, next.chains),
    supportedChainIds: mergeSupportedChainIds(previous.supportedChainIds, next.supportedChainIds),
  };
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return Boolean(value);
}

type ReviewIssuesDashboardUserOptionAliases = ForgeReviewIssuesDashboardResponse & {
  updated_by_options?: string[];
};

function updatedByOptionsFromCrops(dashboard: ForgeReviewIssuesDashboardResponse | null): string[] {
  const snakeOptions = (dashboard as ReviewIssuesDashboardUserOptionAliases | null)
    ?.updated_by_options;
  const rowOptions = dashboard?.crops.map((crop) => crop.updatedBy).filter(isNonEmptyString) ?? [];
  return [
    ...new Set([...(dashboard?.updatedByOptions ?? []), ...(snakeOptions ?? []), ...rowOptions]),
  ].sort();
}

const IssuePill: React.FC<{ issueType: MasterLabelReviewIssueType }> = ({ issueType }) => (
  <span className={`ri-issue-pill is-${issueType.replaceAll('_', '-')}`}>
    {issueType === 'mark_for_deletion' ? (
      <DeleteOutlineOutlinedIcon />
    ) : (
      <ReportProblemOutlinedIcon />
    )}
    {issueLabel(issueType)}
  </span>
);

function assignmentPillClass(row: ForgeReviewIssueCropRow): string {
  if (!row.assignedToDesign) return 'is-unassigned';
  return row.designExists ? 'is-assigned' : 'is-missing-design';
}

function assignmentPillLabel(row: ForgeReviewIssueCropRow): string {
  return row.assignedToDesign ? (row.designExists ? 'Assigned' : 'Missing design') : 'Unassigned';
}

const AssignmentPill: React.FC<{ row: ForgeReviewIssueCropRow }> = ({ row }) => (
  <span
    className={`ri-state-pill ${assignmentPillClass(row)}`}
    aria-label={`Assignment: ${assignmentPillLabel(row)}`}
  >
    {assignmentPillLabel(row)}
  </span>
);

const ReviewIssuesByChainPlot: React.FC<{
  chains: ForgeReviewIssuesChainSnapshot[];
  isLoading: boolean;
  loadError: string | null;
  onSelectChain: (chainId: number) => void;
}> = ({ chains, isLoading, loadError, onSelectChain }) => {
  const maxChainTotal = Math.max(1, ...chains.map((chain) => chain.issueCount));
  const hasMetricData = chains.some((chain) => chain.issueCount > 0);

  const body = (() => {
    if (isLoading) {
      return <div className="ri-chain-plot-status">Loading breakdown...</div>;
    }
    if (loadError) {
      return <div className="ri-chain-plot-status is-error">Breakdown unavailable.</div>;
    }
    if (!hasMetricData) {
      return (
        <div className="ri-chain-plot-status">
          No cached Review Issues counts for supported chains.
        </div>
      );
    }

    return (
      <div className="ri-chain-plot-grid">
        {chains.map((chain) => {
          const total = Math.max(0, chain.issueCount);
          const barWidth = `${Math.max(0.02, total / maxChainTotal) * 100}%`;
          return (
            <div key={chain.chain.chainId} className="ri-chain-plot-row">
              <button
                type="button"
                className="ri-chain-plot-name"
                title={chain.chain.chainName}
                onClick={() => onSelectChain(chain.chain.chainId)}
              >
                <span>{chain.chain.chainName}</span>
                <small>chain {chain.chain.chainId}</small>
              </button>
              <div
                className="ri-chain-plot-track"
                aria-label={`${chain.chain.chainName} Review Issues by type`}
              >
                <div className="ri-chain-plot-bar" style={{ width: barWidth }}>
                  {CHAIN_BREAKDOWN_METRICS.map((metric) => {
                    const value = metric.getValue(chain);
                    const segmentWidth = total > 0 ? `${(value / total) * 100}%` : '0%';
                    return (
                      <div
                        key={metric.key}
                        title={`${metric.label}: ${formatNumber(value)}`}
                        style={{
                          width: segmentWidth,
                          minWidth: value > 0 ? 3 : 0,
                          background: metric.color,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="ri-chain-plot-total">{formatNumber(total)}</div>
            </div>
          );
        })}
      </div>
    );
  })();

  return (
    <section
      className="forge-card rdc-dashboard-card ri-chain-panel"
      aria-label="Review Issues by chain"
    >
      <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title ri-chain-panel-title">
        <div>Review Issues by chain</div>
        <div className="ri-chain-legend">
          {CHAIN_BREAKDOWN_METRICS.map((metric) => (
            <span key={metric.key}>
              <i style={{ background: metric.color }} />
              {metric.label}
            </span>
          ))}
        </div>
      </div>
      {body}
    </section>
  );
};

const SnapshotCell: React.FC<{ chain: ForgeReviewIssuesChainSnapshot }> = ({ chain }) => {
  if (!chain.snapshot) {
    return (
      <span className="ri-chain-snapshot-missing">
        {formatSnapshotStatus(chain.status) ?? 'No cached snapshot'}
      </span>
    );
  }
  const statusLabel = formatSnapshotStatus(chain.status);
  return (
    <div className="ri-chain-snapshot">
      <div>
        <span style={{ color: snapshotAgeColor(chain.snapshot.ageSeconds) }}>
          {formatRelativeAge(chain.snapshot.ageSeconds)}
        </span>
        {statusLabel ? <em>{statusLabel}</em> : null}
      </div>
      <small>{formatTimestamp(chain.snapshot.computedAtUtc)}</small>
    </div>
  );
};

const ChainTable: React.FC<{
  chains: ForgeReviewIssuesChainSnapshot[];
  isLoading: boolean;
  loadError: string | null;
  selectedChainId: number | 'all';
  isRefreshingAll: boolean;
  refreshAllProgress: { completed: number; total: number } | null;
  refreshChainIds: number[];
  refreshingChainIds: Set<number>;
  onSelectChain: (chainId: number | 'all') => void;
  onRefreshChain: (chainId: number) => void;
  onRefreshAll: () => void;
}> = ({
  chains,
  isLoading,
  loadError,
  selectedChainId,
  isRefreshingAll,
  refreshAllProgress,
  refreshChainIds,
  refreshingChainIds,
  onSelectChain,
  onRefreshChain,
  onRefreshAll,
}) => {
  const columnCount = 9;
  return (
    <section className="forge-card rdc-dashboard-card ri-chain-table-card">
      <div className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title ri-chain-table-header">
        <span>Chains</span>
        <button
          type="button"
          className="dg-secondary-button"
          disabled={isRefreshingAll || isLoading || refreshChainIds.length === 0}
          onClick={onRefreshAll}
        >
          <RefreshOutlinedIcon className={isRefreshingAll ? 'is-spinning' : ''} />
          {isRefreshingAll && refreshAllProgress
            ? `Refreshing ${refreshAllProgress.completed}/${refreshAllProgress.total}`
            : 'Refresh all'}
        </button>
      </div>
      <div className="ri-chain-table-scroll">
        <table className="ri-chain-table" aria-label="Review Issues chains table">
          <thead>
            <tr>
              <th>Chain</th>
              <th>Snapshot</th>
              <th>Issues</th>
              <th>Missing design</th>
              <th>Deletion</th>
              <th>Other</th>
              <th>Assigned</th>
              <th>Inactive</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columnCount}>Loading snapshots...</td>
              </tr>
            ) : loadError ? (
              <tr>
                <td colSpan={columnCount} className="ri-chain-table-error">
                  {loadError}
                </td>
              </tr>
            ) : (
              chains.map((chain) => {
                const chainId = chain.chain.chainId;
                const isRefreshing = refreshingChainIds.has(chainId);
                const isRefreshDisabled = isRefreshingAll || isRefreshing;
                return (
                  <tr
                    key={chainId}
                    tabIndex={0}
                    aria-selected={selectedChainId === chainId}
                    className={`ri-chain-table-row ${
                      selectedChainId === chainId ? 'is-selected' : ''
                    }`}
                    onClick={() => onSelectChain(chainId)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelectChain(chainId);
                      }
                    }}
                  >
                    <td>
                      <button
                        type="button"
                        className="ri-chain-table-name"
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelectChain(chainId);
                        }}
                      >
                        <span>{chain.chain.chainName}</span>
                        <small>chain {chainId}</small>
                      </button>
                    </td>
                    <td>
                      <SnapshotCell chain={chain} />
                    </td>
                    <td className="ri-chain-table-mono">{formatNumber(chain.issueCount)}</td>
                    <td className="ri-chain-table-mono">
                      {formatNumber(chain.missingDesignCount)}
                    </td>
                    <td className="ri-chain-table-mono">
                      {formatNumber(chain.markForDeletionCount)}
                    </td>
                    <td className="ri-chain-table-mono">{formatNumber(otherIssueCount(chain))}</td>
                    <td className="ri-chain-table-mono">
                      {formatNumber(chain.assignedToDesignCount)}
                    </td>
                    <td className="ri-chain-table-mono">{formatNumber(chain.inactiveCount)}</td>
                    <td>
                      <button
                        type="button"
                        className="dg-secondary-button"
                        aria-label={`Refresh Review Issues for ${chain.chain.chainName}`}
                        disabled={isRefreshDisabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          onRefreshChain(chainId);
                        }}
                      >
                        <RefreshOutlinedIcon className={isRefreshing ? 'is-spinning' : ''} />
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

interface CropRowProps {
  row: ForgeReviewIssueCropRow;
  isSelected: boolean;
  isDeleting: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

const CropRow: React.FC<CropRowProps> = ({ row, isSelected, isDeleting, onSelect, onDelete }) => (
  <tr
    className={`dg-group-row ri-crop-row ${isSelected ? 'is-selected' : ''}`}
    tabIndex={0}
    onClick={onSelect}
    onKeyDown={(event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect();
      }
    }}
  >
    <td className="dg-row-link-cell">
      {row.masterLabelUrl ? (
        <Link
          className="dg-text-link dg-forge-link"
          to={row.masterLabelUrl}
          aria-label={`Open crop ${shortId(row.cropId)} in Master Label`}
          title="Open in Master Label"
          onClick={(event) => event.stopPropagation()}
        >
          <span aria-hidden="true">[f]</span>
        </Link>
      ) : (
        '-'
      )}
    </td>
    <td>
      <IssuePill issueType={row.reviewIssueType} />
    </td>
    <td>
      <div className="ri-crop-preview">
        {row.previewVizUrl ? <img src={row.previewVizUrl} alt="" loading="lazy" /> : null}
        <span title={row.cropId}>{shortId(row.cropId)}</span>
      </div>
    </td>
    <td>
      <div className="ri-barcode-cell">
        <strong>{row.normalizedBarcode ?? '-'}</strong>
        <span>{row.barcode ?? row.source ?? '-'}</span>
      </div>
    </td>
    <td>
      <div className="ri-design-cell">
        <AssignmentPill row={row} />
        <span title={row.designId ?? undefined}>{shortId(row.designId)}</span>
      </div>
    </td>
    <td>
      <div className="ri-chain-cell">
        <strong>{row.chainName}</strong>
        <span>store {row.storeId}</span>
      </div>
    </td>
    <td>
      <div className="ri-state-stack">
        <span className={`ri-state-pill ${row.isActive ? 'is-active' : 'is-inactive'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
        {row.isPending ? <span className="ri-state-pill is-pending">Pending</span> : null}
      </div>
    </td>
    <td className="dg-row-timestamp">{formatTimestamp(row.updatedAt ?? row.createdAt)}</td>
    <td className="dg-row-user" title={row.updatedBy ?? row.createdBy ?? undefined}>
      {row.updatedBy ?? row.createdBy ?? '-'}
    </td>
    <td className="ri-crop-action-cell">
      {row.reviewIssueType === 'mark_for_deletion' ? (
        <button
          type="button"
          className="dg-secondary-button ri-delete-crop-button"
          disabled={isDeleting}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <DeleteOutlineOutlinedIcon className={isDeleting ? 'is-spinning' : ''} />
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      ) : (
        <span className="ri-action-empty">-</span>
      )}
    </td>
  </tr>
);

const CropDetailPanel: React.FC<{ row: ForgeReviewIssueCropRow }> = ({ row }) => (
  <div className="dg-inline-panel ri-detail-panel">
    <div className="dg-panel-title">
      <LinkOutlinedIcon />
      <span className="dg-panel-title-text" title={row.cropId}>
        Crop {row.cropId}
      </span>
    </div>
    <div className="ri-detail-grid">
      <div>
        <span>Issue</span>
        <strong>{issueLabel(row.reviewIssueType)}</strong>
      </div>
      <div>
        <span>Design</span>
        <strong>{row.designId ?? 'Unassigned'}</strong>
      </div>
      <div>
        <span>Design barcode</span>
        <strong>{row.designNormalizedBarcode ?? '-'}</strong>
      </div>
      <div>
        <span>Representative</span>
        <strong>{shortId(row.designRepresentativeCropId)}</strong>
      </div>
      <div>
        <span>Crop type</span>
        <strong>
          {[row.orientation, row.classificationType].filter(Boolean).join(' / ') || '-'}
        </strong>
      </div>
      <div>
        <span>Source</span>
        <strong>
          {row.forgeOriginSource
            ? `${row.source} from ${row.forgeOriginSource}`
            : (row.source ?? '-')}
        </strong>
      </div>
      <div>
        <span>Image</span>
        <strong>
          {row.imageId
            ? `${row.imageId}${row.imageResolution ? ` / ${row.imageResolution}` : ''}`
            : '-'}
        </strong>
      </div>
      <div>
        <span>Created</span>
        <strong>{formatTimestamp(row.createdAt)}</strong>
      </div>
    </div>
  </div>
);

export const ReviewIssuesDashboard: React.FC = () => {
  const { token } = useAuthContext();
  const { showErrorToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<ForgeReviewIssuesDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedChainId, setSelectedChainId] = useState<number | 'all'>('all');
  const [selectedIssueType, setSelectedIssueType] = useState<MasterLabelReviewIssueType | 'all'>(
    'all'
  );
  const [assignmentFilter, setAssignmentFilter] = useState<ForgeReviewIssueAssignmentFilter>('all');
  const [activeFilter, setActiveFilter] = useState<ForgeReviewIssueActiveFilter>('all');
  const [queryInput, setQueryInput] = useState('');
  const [committedQuery, setCommittedQuery] = useState('');
  const [selectedUpdatedBy, setSelectedUpdatedBy] = useState<string[]>([]);
  const [sort, setSort] = useState<ForgeReviewIssueSort>('updated_newest');
  const [page, setPage] = useState(1);
  const [selectedCropId, setSelectedCropId] = useState<string | null>(null);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [deletingCropIds, setDeletingCropIds] = useState<Set<string>>(() => new Set());
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [refreshAllProgress, setRefreshAllProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const loadRequestSequence = useRef(0);
  const dashboardViewKey = useMemo(
    () =>
      JSON.stringify({
        activeFilter,
        assignmentFilter,
        committedQuery,
        page,
        selectedChainId,
        selectedIssueType,
        selectedUpdatedBy,
        sort,
      }),
    [
      activeFilter,
      assignmentFilter,
      committedQuery,
      page,
      selectedChainId,
      selectedIssueType,
      selectedUpdatedBy,
      sort,
    ]
  );
  const dashboardViewKeyRef = useRef(dashboardViewKey);
  dashboardViewKeyRef.current = dashboardViewKey;

  const loadDashboard = useCallback(async () => {
    const requestSequence = loadRequestSequence.current + 1;
    loadRequestSequence.current = requestSequence;
    setIsLoading(true);
    try {
      const nextDashboard = await ForgeApiService.fetchReviewIssuesDashboard(token, {
        chainId: selectedChainId === 'all' ? null : selectedChainId,
        issueType: selectedIssueType === 'all' ? null : selectedIssueType,
        assignmentFilter,
        activeFilter,
        query: committedQuery || null,
        updatedByValues: selectedUpdatedBy,
        sort,
        page,
        limit: PAGE_LIMIT,
      });
      if (loadRequestSequence.current !== requestSequence) return;
      setDashboard((current) =>
        preserveChainSummariesForSelectedChain(current, nextDashboard, selectedChainId)
      );
      setLoadError(null);
    } catch (error) {
      if (loadRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading Review Issues';
      setDashboard(null);
      setLoadError(message);
      showErrorToast(message);
    } finally {
      if (loadRequestSequence.current === requestSequence) {
        setIsLoading(false);
      }
    }
  }, [
    activeFilter,
    assignmentFilter,
    committedQuery,
    page,
    selectedChainId,
    selectedIssueType,
    selectedUpdatedBy,
    showErrorToast,
    sort,
    token,
  ]);

  const loadDashboardIfViewUnchanged = useCallback(
    async (viewKey: string) => {
      if (dashboardViewKeyRef.current !== viewKey) return;
      await loadDashboard();
    },
    [loadDashboard]
  );

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!dashboard?.crops.length) {
      setSelectedCropId(null);
      return;
    }
    if (selectedCropId && !dashboard.crops.some((crop) => crop.cropId === selectedCropId)) {
      setSelectedCropId(null);
    }
  }, [dashboard?.crops, selectedCropId]);

  const chainOptions = useMemo(() => dashboard?.chains ?? [], [dashboard?.chains]);
  const refreshChainIds = useMemo(
    () => chainOptions.map((chain) => chain.chain.chainId),
    [chainOptions]
  );
  const updatedByOptions = useMemo(() => updatedByOptionsFromCrops(dashboard), [dashboard]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setCommittedQuery(queryInput.trim());
  };

  const handleSelectChain = (chainId: number | 'all') => {
    setSelectedChainId(chainId);
    setPage(1);
  };

  const clearFilters = () => {
    setQueryInput('');
    setCommittedQuery('');
    setSelectedChainId('all');
    setSelectedUpdatedBy([]);
    setSelectedIssueType('all');
    setAssignmentFilter('all');
    setActiveFilter('all');
    setSort('updated_newest');
    setPage(1);
  };

  const beginRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => new Set(current).add(chainId));
  };

  const finishRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => {
      const next = new Set(current);
      next.delete(chainId);
      return next;
    });
  };

  const beginDeletingCrop = (cropId: string) => {
    setDeletingCropIds((current) => new Set(current).add(cropId));
  };

  const finishDeletingCrop = (cropId: string) => {
    setDeletingCropIds((current) => {
      const next = new Set(current);
      next.delete(cropId);
      return next;
    });
  };

  const handleRefreshChain = async (chainId: number) => {
    const actionViewKey = dashboardViewKeyRef.current;
    beginRefreshingChain(chainId);
    try {
      const updatedChain = await ForgeApiService.refreshReviewIssuesDashboard(token, chainId);
      if (dashboardViewKeyRef.current !== actionViewKey) return;
      setDashboard((current) => (current ? recomputeDashboard(current, updatedChain) : current));
      await loadDashboardIfViewUnchanged(actionViewKey);
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error refreshing Review Issues');
    } finally {
      finishRefreshingChain(chainId);
    }
  };

  const handleRefreshAll = async () => {
    if (refreshChainIds.length === 0 || isRefreshingAll) return;
    if (!window.confirm(REFRESH_ALL_CONFIRMATION_MESSAGE)) return;

    const actionViewKey = dashboardViewKeyRef.current;
    setIsRefreshingAll(true);
    setRefreshAllProgress({ completed: 0, total: refreshChainIds.length });
    const failures: string[] = [];

    for (const [index, chainId] of refreshChainIds.entries()) {
      beginRefreshingChain(chainId);
      try {
        const updatedChain = await ForgeApiService.refreshReviewIssuesDashboard(token, chainId);
        if (dashboardViewKeyRef.current === actionViewKey) {
          setDashboard((current) =>
            current ? recomputeDashboard(current, updatedChain) : current
          );
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Error refreshing Review Issues';
        failures.push(`chain ${chainId}: ${message}`);
      } finally {
        finishRefreshingChain(chainId);
        setRefreshAllProgress({ completed: index + 1, total: refreshChainIds.length });
      }
    }

    setIsRefreshingAll(false);
    setRefreshAllProgress(null);
    if (failures.length > 0) {
      showErrorToast(
        `Refresh all finished with ${failures.length}/${refreshChainIds.length} failures. ${failures[0]}`
      );
      return;
    }
    await loadDashboardIfViewUnchanged(actionViewKey);
  };

  const handleDeleteCrop = async (row: ForgeReviewIssueCropRow) => {
    if (row.reviewIssueType !== 'mark_for_deletion' || deletingCropIds.has(row.cropId)) return;
    if (!window.confirm(`${DELETE_CROP_CONFIRMATION_MESSAGE}\n\nCrop: ${shortId(row.cropId)}`)) {
      return;
    }

    const actionViewKey = dashboardViewKeyRef.current;
    beginDeletingCrop(row.cropId);
    try {
      await ForgeApiService.deleteCrops(token, [row.cropId]);
      if (dashboardViewKeyRef.current === actionViewKey) {
        setSelectedCropId((current) => (current === row.cropId ? null : current));
        setDashboard((current) =>
          current
            ? {
                ...current,
                crops: current.crops.filter((crop) => crop.cropId !== row.cropId),
                total: Math.max(0, current.total - 1),
              }
            : current
        );
      }
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Error deleting crop');
      finishDeletingCrop(row.cropId);
      return;
    }

    beginRefreshingChain(row.chainId);
    try {
      const updatedChain = await ForgeApiService.refreshReviewIssuesDashboard(token, row.chainId);
      if (dashboardViewKeyRef.current !== actionViewKey) return;
      setDashboard((current) => (current ? recomputeDashboard(current, updatedChain) : current));
      await loadDashboardIfViewUnchanged(actionViewKey);
    } catch (error) {
      showErrorToast(
        error instanceof Error
          ? `Crop deleted, but Review Issues refresh failed: ${error.message}`
          : 'Crop deleted, but Review Issues refresh failed'
      );
    } finally {
      finishRefreshingChain(row.chainId);
      finishDeletingCrop(row.cropId);
    }
  };

  const hasListFilters =
    Boolean(committedQuery) ||
    selectedChainId !== 'all' ||
    selectedUpdatedBy.length > 0 ||
    selectedIssueType !== 'all' ||
    assignmentFilter !== 'all' ||
    activeFilter !== 'all' ||
    sort !== 'updated_newest';

  return (
    <div className="page-content page-content--wide dg-dashboard-page ri-dashboard-page">
      <section className="forge-card dg-dashboard-hero ri-dashboard-hero">
        <div className="dg-hero-main">
          <div>
            <div className="dg-dashboard-title">Review Issues</div>
            <div className="rdc-dashboard-copy">
              Persisted crop review issues grouped by chain, assignment state, and issue type.
            </div>
            <div className="dg-hero-metrics">
              <span>
                <strong>{formatNumber(totalIssues(dashboard?.chains ?? []))}</strong>
                issues
              </span>
              <span>
                <strong>{formatNumber(totalAssigned(dashboard?.chains ?? []))}</strong>
                assigned
              </span>
              <span>
                <strong>{formatNumber(totalMissingDesign(dashboard?.chains ?? []))}</strong>
                missing design
              </span>
              <span>
                <strong>{formatNumber(totalMarkedForDeletion(dashboard?.chains ?? []))}</strong>
                deletion
              </span>
            </div>
          </div>
        </div>
        <div className="dg-filter-row">
          <div className="dg-filter-group">
            <FilterListOutlinedIcon />
            <select
              aria-label="Review Issues chain filter"
              value={selectedChainId}
              onChange={(event) =>
                handleSelectChain(event.target.value === 'all' ? 'all' : Number(event.target.value))
              }
            >
              <option value="all">All supported chains</option>
              {chainOptions.map((chain) => (
                <option key={chain.chain.chainId} value={chain.chain.chainId}>
                  {chain.chain.chainName}
                </option>
              ))}
            </select>
          </div>
          <div className="dg-filter-group">
            <select
              aria-label="Review Issues assignment filter"
              value={assignmentFilter}
              onChange={(event) => {
                setAssignmentFilter(event.target.value as ForgeReviewIssueAssignmentFilter);
                setPage(1);
              }}
            >
              {ASSIGNMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="dg-filter-group">
            <select
              aria-label="Review Issues active filter"
              value={activeFilter}
              onChange={(event) => {
                setActiveFilter(event.target.value as ForgeReviewIssueActiveFilter);
                setPage(1);
              }}
            >
              {ACTIVE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="dg-secondary-button"
            disabled={isLoading}
            onClick={() => void loadDashboard()}
          >
            <RefreshOutlinedIcon className={isLoading ? 'is-spinning' : ''} />
            Refresh view
          </button>
        </div>
      </section>

      <ReviewIssuesByChainPlot
        chains={chainOptions}
        isLoading={isLoading}
        loadError={loadError}
        onSelectChain={(chainId) => handleSelectChain(chainId)}
      />

      <ChainTable
        chains={chainOptions}
        isLoading={isLoading}
        loadError={loadError}
        selectedChainId={selectedChainId}
        isRefreshingAll={isRefreshingAll}
        refreshAllProgress={refreshAllProgress}
        refreshChainIds={refreshChainIds}
        refreshingChainIds={refreshingChainIds}
        onSelectChain={handleSelectChain}
        onRefreshChain={(chainId) => void handleRefreshChain(chainId)}
        onRefreshAll={() => void handleRefreshAll()}
      />

      <section className="dg-dashboard-grid ri-dashboard-grid">
        <div className="forge-card dg-list-panel">
          <div className="dg-list-header">
            <div>
              <div className="dg-panel-title">Crops</div>
              <div className="dg-list-count">
                Showing{' '}
                {visibleRange(
                  dashboard?.page ?? page,
                  dashboard?.limit ?? PAGE_LIMIT,
                  dashboard?.total ?? 0
                )}{' '}
                of {formatNumber(dashboard?.total ?? 0)}
              </div>
            </div>
            <div className="dg-pagination">
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous page"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <span>Page {formatNumber(page)}</span>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next page"
                disabled={!dashboard?.hasNext || isLoading}
                onClick={() => setPage((current) => current + 1)}
              >
                <ChevronRightOutlinedIcon />
              </button>
            </div>
            <form className="dg-list-controls ri-list-controls" onSubmit={handleSearchSubmit}>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Search Review Issues"
                  placeholder="Crop ID, design ID, barcode, or user"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                />
              </div>
              <label className="dg-sort-control">
                <span>Issue</span>
                <select
                  aria-label="Filter Review Issues by issue type"
                  value={selectedIssueType}
                  onChange={(event) => {
                    setSelectedIssueType(event.target.value as MasterLabelReviewIssueType | 'all');
                    setPage(1);
                  }}
                >
                  {ISSUE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <DashboardEmailFilter
                ariaLabel="Filter Review Issues by updated user"
                options={updatedByOptions}
                selected={selectedUpdatedBy}
                onChange={(nextSelected) => {
                  setSelectedUpdatedBy(nextSelected);
                  setPage(1);
                }}
              />
              <label className="dg-sort-control">
                <span>Sort</span>
                <select
                  aria-label="Sort Review Issues"
                  value={sort}
                  onChange={(event) => {
                    setSort(event.target.value as ForgeReviewIssueSort);
                    setPage(1);
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {hasListFilters ? (
                <button type="button" className="dg-search-clear" onClick={clearFilters}>
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>
          <div className="dg-scope-tabs ri-issue-tabs" role="group" aria-label="Review issue type">
            {ISSUE_TYPE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={selectedIssueType === option.value ? 'is-active' : ''}
                onClick={() => {
                  setSelectedIssueType(option.value);
                  setPage(1);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="dg-group-list">
            {isLoading ? (
              <div className="dg-loading-state">Loading Review Issues...</div>
            ) : loadError ? (
              <div className="dg-empty-state">
                <strong>Review Issues could not be loaded</strong>
                <span>{loadError}</span>
                <button
                  type="button"
                  className="dg-secondary-button"
                  onClick={() => void loadDashboard()}
                >
                  Retry
                </button>
              </div>
            ) : dashboard?.crops.length ? (
              <div className="dg-group-table-scroll ri-table-scroll">
                <table className="dg-group-table ri-issue-table" aria-label="Review Issues table">
                  <thead>
                    <tr>
                      <th>Link</th>
                      <th>Issue</th>
                      <th>Crop</th>
                      <th>Barcode</th>
                      <th>Design</th>
                      <th>Chain</th>
                      <th>State</th>
                      <th>Updated</th>
                      <th>Updated by</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.crops.map((row) => (
                      <React.Fragment key={row.cropId}>
                        <CropRow
                          row={row}
                          isSelected={row.cropId === selectedCropId}
                          isDeleting={deletingCropIds.has(row.cropId)}
                          onSelect={() => setSelectedCropId(row.cropId)}
                          onDelete={() => void handleDeleteCrop(row)}
                        />
                        {row.cropId === selectedCropId ? (
                          <tr className="dg-inline-detail-row">
                            <td colSpan={10}>
                              <CropDetailPanel row={row} />
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="dg-empty-state">No Review Issues match this view.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
